import React, { useCallback, useMemo } from "react";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useTheme, Box, Typography, IconButton, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import styled from "@emotion/styled";
import DashboardToolbar from "./DashboardToolbar";
import WidgetRegistry from "../widgets/WidgetRegistry";
import ErrorBoundary from "./ErrorBoundary";

const ResponsiveGridLayout = WidthProvider(Responsive);

const EditorContainer = styled(Box)({
    width: "100%",
});

const WidgetWrapper = styled(Box)({
    width: "100%",
    height: "100%",
});

const EmptyState = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(4),
    textAlign: "center",
}));

const EmptyBody = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    maxWidth: 420,
    margin: theme.spacing(1, 0, 3),
}));

const EmptyAction = styled(Button)(({ theme }) => ({
    [theme.breakpoints.down("sm")]: {
        width: "100%",
        maxWidth: 320,
    },
}));

const PreviewCard = styled(Box)(({ theme }) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px dashed ${theme.palette.divider}`,
    padding: theme.spacing(2),
    position: "relative",
    textAlign: "center",
}));

const PreviewRemoveButton = styled(IconButton)(({ theme }) => ({
    position: "absolute",
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
}));

const PreviewIcon = styled(Box)(({ theme }) => ({
    fontSize: 36,
    marginBottom: theme.spacing(1),
    opacity: 0.7,
}));

export const generateWidgetId = () =>
    `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const DashboardEditor = ({
    layout,
    widgets,
    isLocked,
    permissions,
    onChange,
    onCommit,
    onToggleLock,
    onWidgetConfigChange,
    previewMode = false,
    showToolbar = true,
    toolbarTitle,
    showResetLayout = true,
    showLockToggle = true,
    emptyTitle = "Welcome to your Dashboard",
    emptyBody = "Add widgets to track tasks, projects, and team activity at a glance.",
    emptyEditingBody = 'Click "+ Add Widget" above to start building.',
}) => {
    const theme = useTheme();

    const Breakpoints = useMemo(() => ({
        xl: theme.breakpoints.values.xl,
        lg: theme.breakpoints.values.lg,
        md: theme.breakpoints.values.md,
        sm: theme.breakpoints.values.sm,
        xs: theme.breakpoints.values.xs,
        xxs: 0,
    }), [theme]);

    const mergeLayoutPositions = useCallback((newLayout) => layout.map((existing) => {
        const updated = newLayout.find((item) => item.i === existing.i);
        if (!updated) return existing;
        return { ...existing, x: updated.x, y: updated.y, w: updated.w, h: updated.h };
    }), [layout]);

    const handleLayoutChange = useCallback((newLayout) => {
        const merged = mergeLayoutPositions(newLayout);
        onChange?.({ layout: merged, widgets });
    }, [mergeLayoutPositions, onChange, widgets]);

    const handleLayoutCommit = useCallback((newLayout) => {
        const merged = mergeLayoutPositions(newLayout);
        onCommit?.({ layout: merged, widgets });
    }, [mergeLayoutPositions, onCommit, widgets]);

    const handleAddWidget = useCallback((newWidget) => {
        const newLayout = [...layout, newWidget];
        const newWidgets = {
            ...widgets,
            [newWidget.i]: { type: newWidget.widgetType, config: {} },
        };
        onChange?.({ layout: newLayout, widgets: newWidgets });
        onCommit?.({ layout: newLayout, widgets: newWidgets });
    }, [layout, widgets, onChange, onCommit]);

    const handleRemoveWidget = useCallback((widgetId) => {
        const newLayout = layout.filter((item) => item.i !== widgetId);
        const newWidgets = { ...widgets };
        delete newWidgets[widgetId];
        onChange?.({ layout: newLayout, widgets: newWidgets });
        onCommit?.({ layout: newLayout, widgets: newWidgets });
    }, [layout, widgets, onChange, onCommit]);

    const handleResetLayout = useCallback(() => {
        onChange?.({ layout: [], widgets: {} });
        onCommit?.({ layout: [], widgets: {} });
    }, [onChange, onCommit]);

    const renderWidget = useCallback((item) => {
        const widgetData = widgets[item.i];
        if (!widgetData) return null;

        if (previewMode) {
            const def = WidgetRegistry.getById(widgetData.type);
            const Icon = def?.icon;
            return (
                <PreviewCard>
                    {!isLocked && (
                        <PreviewRemoveButton
                            size="small"
                            className="widget-no-drag"
                            onClick={() => handleRemoveWidget(item.i)}
                            aria-label="remove widget"
                        >
                            <CloseIcon fontSize="small" />
                        </PreviewRemoveButton>
                    )}
                    {Icon && <PreviewIcon as={Icon} />}
                    <Typography variant="subtitle2">{def?.name || widgetData.type}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {def?.description || ""}
                    </Typography>
                </PreviewCard>
            );
        }

        const WidgetComponent = WidgetRegistry.getComponent(widgetData.type);
        if (!WidgetComponent) return null;
        if (!WidgetRegistry.isWidgetAvailable(widgetData.type, permissions)) return null;

        return (
            <ErrorBoundary variant="widget" label={WidgetRegistry.getById(widgetData.type)?.name}>
                <WidgetComponent
                    widgetId={item.i}
                    config={widgetData.config}
                    onConfigChange={onWidgetConfigChange ? (patch) => onWidgetConfigChange(item.i, patch) : null}
                    onRemove={isLocked ? null : handleRemoveWidget}
                    isLocked={isLocked}
                />
            </ErrorBoundary>
        );
    }, [widgets, permissions, handleRemoveWidget, isLocked, previewMode, onWidgetConfigChange]);

    // A layout is saved once and read forever, so a tile whose widget later grew its declared
    // minimum would keep rendering clipped. Grow it to the minimum instead of trusting the
    // stored size, and hand the minimum to the grid so it cannot be dragged back below it.
    const addedWidgetTypes = useMemo(
        () => Object.values(widgets || {}).map((entry) => entry?.type).filter(Boolean),
        [widgets],
    );

    const validLayoutItems = useMemo(() => layout.filter((item) => {
        const widgetData = widgets[item.i];
        if (!widgetData) return false;
        if (!WidgetRegistry.isWidgetAvailable(widgetData.type, permissions)) return false;
        if (previewMode) return WidgetRegistry.getById(widgetData.type) != null;
        return WidgetRegistry.getComponent(widgetData.type) != null;
    }).map((item) => {
        const minSize = WidgetRegistry.getById(widgets[item.i].type)?.minSize;
        if (!minSize) return item;
        return {
            ...item,
            minW: minSize.w,
            minH: minSize.h,
            w: Math.max(item.w || 0, minSize.w),
            h: Math.max(item.h || 0, minSize.h),
        };
    }), [layout, widgets, permissions, previewMode]);

    const layouts = useMemo(() => ({
        xl: validLayoutItems, lg: validLayoutItems, md: validLayoutItems, sm: validLayoutItems, xs: validLayoutItems, xxs: validLayoutItems,
    }), [validLayoutItems]);

    return (
        <EditorContainer>
            {showToolbar && (
                <DashboardToolbar
                    onAddWidget={handleAddWidget}
                    onResetLayout={handleResetLayout}
                    isLocked={isLocked}
                    onToggleLock={onToggleLock}
                    userPermissions={permissions}
                    addedWidgetTypes={addedWidgetTypes}
                    title={toolbarTitle}
                    showResetLayout={showResetLayout}
                    showLockToggle={showLockToggle}
                />
            )}

            {validLayoutItems.length === 0 ? (
                <EmptyState>
                    <Typography variant="h5">{emptyTitle}</Typography>
                    <EmptyBody variant="body1">
                        {isLocked ? emptyBody : emptyEditingBody}
                    </EmptyBody>
                    {isLocked && showLockToggle && onToggleLock && (
                        <EmptyAction
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<TuneIcon />}
                            onClick={onToggleLock}
                        >
                            Customize Dashboard
                        </EmptyAction>
                    )}
                </EmptyState>
            ) : (
                <ResponsiveGridLayout
                    className="layout"
                    layouts={layouts}
                    onLayoutChange={handleLayoutChange}
                    onDragStop={handleLayoutCommit}
                    onResizeStop={handleLayoutCommit}
                    breakpoints={Breakpoints}
                    cols={{ xl: 12, lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
                    rowHeight={30}
                    isDraggable={!isLocked}
                    isResizable={!isLocked}
                    compactType="vertical"
                    preventCollision={false}
                    draggableCancel=".widget-no-drag"
                >
                    {validLayoutItems.map((item) => (
                        <div key={item.i}>
                            <WidgetWrapper>{renderWidget(item)}</WidgetWrapper>
                        </div>
                    ))}
                </ResponsiveGridLayout>
            )}
        </EditorContainer>
    );
};

export default DashboardEditor;
